// src/components/profiles/filters/AdvancedFilters.jsx
import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Chip,
  Stack,
  TextField,
  Autocomplete
} from '@mui/material';

const AdvancedFilters = ({ filters, onChange, options }) => {
  const handleChange = (field, value) => {
    onChange({
      ...filters,
      [field]: value
    });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Advanced Filters
      </Typography>
      
      <Grid container spacing={3}>
        {/* Religious Filters */}
        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Religion
            </Typography>
            <Select
              fullWidth
              size="small"
              value={filters.religion || ''}
              onChange={(e) => handleChange('religion', e.target.value || '')}
              displayEmpty
            >
              <MenuItem value="">All Religions</MenuItem>
              {options.religions.map((religion) => (
                <MenuItem key={religion} value={religion}>{religion}</MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sect
            </Typography>
            <Select
              fullWidth
              size="small"
              value={filters.sect || ''}
              onChange={(e) => handleChange('sect', e.target.value || '')}
              displayEmpty
            >
              <MenuItem value="">All Sects</MenuItem>
              <MenuItem value="Sunni">Sunni</MenuItem>
              <MenuItem value="Shia">Shia</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Caste
            </Typography>
            <Select
              fullWidth
              size="small"
              value={filters.caste || ''}
              onChange={(e) => handleChange('caste', e.target.value || '')}
              displayEmpty
            >
              <MenuItem value="">All Castes</MenuItem>
              {options.castes.map((caste) => (
                <MenuItem key={caste} value={caste}>{caste}</MenuItem>
              ))}
            </Select>
          </Box>
        </Grid>

        {/* Family Filters */}
        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Family Type
            </Typography>
            <Select
              fullWidth
              size="small"
              value={filters.familyType || ''}
              onChange={(e) => handleChange('familyType', e.target.value || '')}
              displayEmpty
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="Joint">Joint</MenuItem>
              <MenuItem value="Nuclear">Nuclear</MenuItem>
            </Select>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Family Status
            </Typography>
            <Select
              fullWidth
              size="small"
              value={filters.familyStatus || ''}
              onChange={(e) => handleChange('familyStatus', e.target.value || '')}
              displayEmpty
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="Middle Class">Middle Class</MenuItem>
              <MenuItem value="Upper Middle Class">Upper Middle Class</MenuItem>
              <MenuItem value="Wealthy">Wealthy</MenuItem>
            </Select>
          </Box>
        </Grid>

        {/* Income Range */}
        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Monthly Income: {filters.incomeRange?.[0]?.toLocaleString()} - {filters.incomeRange?.[1]?.toLocaleString()} PKR
            </Typography>
            <Slider
              value={filters.incomeRange || [0, 500000]}
              onChange={(e, newValue) => handleChange('incomeRange', newValue)}
              valueLabelDisplay="auto"
              min={0}
              max={500000}
              step={10000}
              marks={[
                { value: 0, label: '0' },
                { value: 100000, label: '100K' },
                { value: 300000, label: '300K' },
                { value: 500000, label: '500K' }
              ]}
            />
          </Box>
        </Grid>

        {/* Height Range */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Height Range
            </Typography>
            <Stack direction="row" spacing={1}>
              {['4ft', '5ft', '5ft5', '6ft'].map((height) => (
                <Chip
                  key={height}
                  label={height}
                  onClick={() => handleChange('height', height)}
                  color={filters.height === height ? 'primary' : 'default'}
                  variant={filters.height === height ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        </Grid>

        {/* Body Type */}
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Body Type
            </Typography>
            <Select
              fullWidth
              size="small"
              value={filters.bodyType || ''}
              onChange={(e) => handleChange('bodyType', e.target.value || '')}
              displayEmpty
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="Slim">Slim</MenuItem>
              <MenuItem value="Average">Average</MenuItem>
              <MenuItem value="Athletic">Athletic</MenuItem>
              <MenuItem value="Heavy">Heavy</MenuItem>
            </Select>
          </Box>
        </Grid>

        {/* Hobbies */}
        <Grid item xs={12}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Hobbies & Interests
            </Typography>
            <Autocomplete
              multiple
              options={options.hobbies || []}
              value={filters.hobbies || []}
              onChange={(event, newValue) => handleChange('hobbies', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Select hobbies"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
          </Box>
        </Grid>

        {/* Languages */}
        <Grid item xs={12}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Languages Known
            </Typography>
            <Autocomplete
              multiple
              options={options.languages || []}
              value={filters.languages || []}
              onChange={(event, newValue) => handleChange('languages', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder="Select languages"
                />
              )}
            />
          </Box>
        </Grid>

        {/* Lifestyle */}
        <Grid item xs={12}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Lifestyle
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {['Smoking', 'Drinking', 'Vegetarian', 'Non-vegetarian', 'Fitness'].map((habit) => (
                <Chip
                  key={habit}
                  label={habit}
                  onClick={() => {
                    const currentHabits = filters.lifestyleHabits || [];
                    const newHabits = currentHabits.includes(habit)
                      ? currentHabits.filter(h => h !== habit)
                      : [...currentHabits, habit];
                    handleChange('lifestyleHabits', newHabits);
                  }}
                  color={(filters.lifestyleHabits || []).includes(habit) ? 'primary' : 'default'}
                  variant={(filters.lifestyleHabits || []).includes(habit) ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AdvancedFilters;